import React, { useContext } from 'react'

import moment from 'moment'
import { identity, isEmpty } from 'ramda'

import { isRunningInBrowser } from 'config/platform'
import { OrgUsersById, OrgUsersContext,User } from 'context/orgUsersContext'
import {
  Activity,
  ActivityContext,
  ActivityForContact,
  ActivityType
} from 'modules/accountActivity/domain'
import { conversationUrl } from 'utils/paths'
import { prefixUrlWithHttp, urlForCampaign } from 'utils/urlUtils'

import styles from './styles.module.css'

interface FormattedActivityProps {
  activity: Activity | ActivityForContact
  email?: string
}

interface LinkProps {
  context: ActivityContext
}

const formatTime = (startTime: number) => moment(startTime).format('MMMM Do YYYY [at] h:mm A')

const getUserName = (user: User | undefined) => user?.name || user?.email

const getMeetingTypeStr = (context: ActivityContext) =>
  context?.meetingType ? ` "${context?.meetingType}"` : ''

const getMeetingTimeStr = (context: ActivityContext) =>
  context?.slotStart ? ` for ${formatTime(context?.slotStart)}` : ''

const getParticipantsStr = (context: ActivityContext, orgUsersById: OrgUsersById) => {
  const agents = participantsToString(context, orgUsersById)
  return !isEmpty(agents) ? ` with ${agents}` : ''
}

const getPlaybookStr = (context: ActivityContext) =>
  context.playbookName ? ` "${context.playbookName}` : ''

const getPercentageStr = (context: ActivityContext) => {
  const percentage = context.percentViewed
  return percentage ? ` ${percentage}% of` : ''
}

export const participantsToString = (context: ActivityContext, orgUsersById: OrgUsersById) => {
  const agentIds = [...(context?.agentParticipants || []), ...(context.participantIds || [])]
  return agentIds
    ?.map(id => getUserName(orgUsersById[id]))
    .filter(identity)
    .join(', ')
}

const ConversationLink: React.FC<LinkProps> = ({ context }) => {
  return context?.id ? (
    <a
      className={styles.conversationLink}
      target={isRunningInBrowser ? "_blank" : ""}
      rel="noopener noreferrer"
      href={conversationUrl(context.id)}>
      "{context?.conversationPreview}"
    </a>
  ) : null
}

const PageLink: React.FC<LinkProps> = ({ context }) => {
  const activityTitle = context.pageTitle || context.title
  const activityUrl = context.pageUrl || context.url
  return activityUrl ? (
    <a
      className={styles.activityLink}
      href={prefixUrlWithHttp(activityUrl)}
      target="_blank"
      rel="noreferrer noopener"
      onClick={e => e.stopPropagation()}>
      {activityTitle ?? activityUrl}
    </a>
  ) : null
}

const EmailLink: React.FC<LinkProps> = ({ context }) => {
  return context?.campaignId && context?.subject ? (
    <a
      className={styles.conversationLink}
      target="_blank"
      rel="noopener noreferrer"
      href={urlForCampaign(context?.campaignId)}>
      "{context.subject}"
    </a>
  ) : null
}

// Formats activities based on included types
// https://github.com/Driftt/account-activity-api/blob/e8a7c12ea82a50935fadacc44e558116728e7aa4/AccountActivityWorkers/src/main/java/com/driftt/account_activity/cards/grouping/ActivityGroupProcessor.java#L42
export const formattedActivity = (
  activity: Activity | ActivityForContact,
  orgUsersById: OrgUsersById
) => {
  const context = activity.activityContext
  switch (activity.activityType) {
    case ActivityType.PAGE_VIEW:
      return (
        <span>
          viewed <PageLink context={context} />
        </span>
      )
    case ActivityType.VIDEO: {
      const percentage = getPercentageStr(context)
      return (
        <span>
          watched{percentage} <PageLink context={context} />
        </span>
      )
    }
    case ActivityType.CONVERSATION: {
      const participants = getParticipantsStr(context, orgUsersById)
      return (
        <span>
          said: <ConversationLink context={context} /> in a conversation{participants}
        </span>
      )
    }
    case ActivityType.EMAIL_CLICK: {
      const playbookName = getPlaybookStr(context)
      return (
        <span>
          cicked a link in email campaign
          <EmailLink context={context} /> sent from playbook{playbookName}
        </span>
      )
    }
    case ActivityType.EMAIL_OPEN: {
      const playbookName = getPlaybookStr(context)
      return (
        <span>
          opened email campaign <EmailLink context={context} /> sent from playbook{playbookName}
        </span>
      )
    }
    case ActivityType.EMAIL_SEND: {
      const senderEmail = activity.activityContext?.taskContext?.senderEmail
      const recipientEmail = activity.activityContext?.taskContext?.recipientEmail
      const subject = context.subject
      return (
        <span>
         {senderEmail} sent an email: {subject}
         {!!recipientEmail && <span> to: {recipientEmail}</span>}
        </span>
      )
    }
    case ActivityType.MEETING_BOOKED: {
      const meetingTime = getMeetingTimeStr(context)
      const meetingType = getMeetingTypeStr(context)
      const participants = getParticipantsStr(context, orgUsersById)
      return (
        <span>
          booked a{meetingType} meeting{participants}
          {meetingTime}
        </span>
      )
    }
    case ActivityType.MEETING_RECORDED: {
      const participants = getParticipantsStr(context, orgUsersById)
      return <span>Recorded a meeting{participants}</span>
    }
  }
}

const FormattedActivity: React.FC<FormattedActivityProps> = ({ activity }) => {
  const orgUsersById = useContext<OrgUsersById>(OrgUsersContext)

  return (
    <div className={styles.activityDescription}>
      <span className={styles.textNowrap}>{formattedActivity(activity, orgUsersById)}</span>
    </div>
  )
}

export default FormattedActivity
